// Servicio para publicaciones masivas con Firebase
// Las funciones de Firebase se obtienen desde window.auth

// Declarar window.auth para TypeScript
declare global {
  interface Window {
    auth: any;
  }
}

// Interfaces para los datos
export interface ProductData {
  id: string;
  sku: string;
  name: string;
  description: string;
  price: number;
  category: string;
  brand?: string;
  stock: number;
  variants?: any[];
  path?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductImage {
  id: string;
  url: string;
  name: string;
  type: 'front' | 'back' | 'closeup' | 'variant';
  selected: boolean;
}

export interface Platform {
  id: string;
  name: string;
  type: 'ecommerce' | 'social';
  connected: boolean;
  selected: boolean;
}

// Función helper para obtener datos mock
const getMockProducts = (): ProductData[] => [
  {
    id: 'prod-1',
    sku: 'SKU-001',
    name: 'Camiseta Básica Algodón',
    description: 'Camiseta de algodón 100% orgánico, cómoda y suave al tacto.',
    price: 25.99,
    category: 'ropa',
    brand: 'EcoWear',
    stock: 50,
    variants: [
      { color: 'Blanco', size: 'M', stock: 15 },
      { color: 'Negro', size: 'L', stock: 20 },
      { color: 'Azul', size: 'S', stock: 15 }
    ],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: 'prod-2',
    sku: 'SKU-002',
    name: 'Pantalón Deportivo',
    description: 'Pantalón deportivo con tecnología de secado rápido.',
    price: 45.99,
    category: 'deportes',
    brand: 'SportMax',
    stock: 30,
    variants: [
      { color: 'Negro', size: 'M', stock: 10 },
      { color: 'Gris', size: 'L', stock: 20 }
    ],
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-18')
  }
];

const getMockImages = (): ProductImage[] => [
  {
    id: 'img-1',
    url: 'https://via.placeholder.com/300x400/4F46E5/FFFFFF?text=Front',
    name: 'front-image.jpg',
    type: 'front',
    selected: true
  },
  {
    id: 'img-2',
    url: 'https://via.placeholder.com/300x400/059669/FFFFFF?text=Back',
    name: 'back-image.jpg',
    type: 'back',
    selected: false
  },
  {
    id: 'img-3',
    url: 'https://via.placeholder.com/300x400/DC2626/FFFFFF?text=Close+Up',
    name: 'closeup-1.jpg',
    type: 'closeup',
    selected: false
  },
  {
    id: 'img-4',
    url: 'https://via.placeholder.com/300x400/7C3AED/FFFFFF?text=Variant',
    name: 'variant-1.jpg',
    type: 'variant',
    selected: false
  }
];

const getMockPlatforms = (): Platform[] => [
  {
    id: 'tiendanube',
    name: 'Tienda Nube',
    type: 'ecommerce',
    connected: true,
    selected: false
  },
  {
    id: 'mercadolibre',
    name: 'MercadoLibre',
    type: 'ecommerce',
    connected: true,
    selected: false
  },
  {
    id: 'instagram',
    name: 'Instagram',
    type: 'social',
    connected: true,
    selected: false
  },
  {
    id: 'facebook',
    name: 'Facebook',
    type: 'social',
    connected: true,
    selected: false
  }
];

// Servicio para cargar productos desde Firestore
export const loadProductsFromFirestore = async (userId: string): Promise<ProductData[]> => {
  try {
    console.log('🔄 Cargando productos desde Firestore para usuario:', userId);
    
    // Verificar si Firebase está disponible
    if (!window.auth?.db || !window.auth?.collection || !window.auth?.getDocs) {
      console.warn('⚠️ Firebase no está disponible, usando datos de ejemplo');
      return getMockProducts();
    }
    
    // TODO: Implementar consulta real a Firestore
    // Por ahora retornamos datos de ejemplo
    const products = getMockProducts();

    console.log('✅ Productos cargados:', products.length);
    return products;
  } catch (error) {
    console.error('❌ Error cargando productos:', error);
    throw error;
  }
};

// Servicio para cargar imágenes desde Storage
export const loadProductImagesFromStorage = async (userId: string, productSku: string): Promise<ProductImage[]> => {
  try {
    console.log('🔄 Cargando imágenes desde Storage para producto:', productSku);
    
    // Verificar si Firebase Storage está disponible
    if (!window.auth?.storage || !window.auth?.ref || !window.auth?.listAll || !window.auth?.getDownloadURL) {
      console.warn('⚠️ Firebase Storage no está disponible, usando imágenes de ejemplo');
      return getMockImages();
    }
    
    // TODO: Implementar consulta real a Firebase Storage
    // Por ahora retornamos imágenes de ejemplo
    const images = getMockImages();

    console.log('✅ Imágenes cargadas:', images.length);
    return images;
  } catch (error) {
    console.error('❌ Error cargando imágenes:', error);
    throw error;
  }
};

// Servicio para obtener plataformas conectadas
export const getConnectedPlatforms = async (userId: string): Promise<Platform[]> => {
  try {
    console.log('🔄 Obteniendo plataformas conectadas para usuario:', userId);
    
    // Verificar si Firebase está disponible
    if (!window.auth?.db || !window.auth?.collection || !window.auth?.getDocs) {
      console.warn('⚠️ Firebase no está disponible, usando plataformas de ejemplo');
      return getMockPlatforms();
    }
    
    // TODO: Implementar consulta real a la configuración del usuario
    // Por ahora retornamos plataformas de ejemplo
    const platforms = getMockPlatforms();

    console.log('✅ Plataformas obtenidas:', platforms.length);
    return platforms;
  } catch (error) {
    console.error('❌ Error obteniendo plataformas:', error);
    throw error;
  }
};

// Servicio para generar descripción con IA (simulado)
export const generateDescriptionWithAI = async (productData: ProductData, customTags?: string[]): Promise<string> => {
  try {
    console.log('🤖 Simulacro de generación con IA inicializado');
    console.log('📝 Datos del producto:', productData);
    console.log('🏷️ Etiquetas personalizadas:', customTags);
    
    // TODO: Implementar llamada real a API de IA
    // Por ahora retornamos una descripción simulada
    const simulatedDescription = `Descripción optimizada para SEO: ${productData.description} ${customTags?.join(', ') || ''}`;
    
    console.log('✅ Descripción generada:', simulatedDescription);
    return simulatedDescription;
  } catch (error) {
    console.error('❌ Error generando descripción:', error);
    throw error;
  }
};

// Servicio para generar caption con IA (simulado)
export const generateCaptionWithAI = async (productData: ProductData, hashtags?: string[]): Promise<string> => {
  try {
    console.log('🤖 Simulacro de generación con IA inicializado');
    console.log('📝 Datos del producto:', productData);
    console.log('#️⃣ Hashtags:', hashtags);
    
    // TODO: Implementar llamada real a API de IA
    // Por ahora retornamos un caption simulado
    const simulatedCaption = `¡Descubre ${productData.name}! ${productData.description} ${hashtags?.map(tag => `#${tag}`).join(' ') || ''}`;
    
    console.log('✅ Caption generado:', simulatedCaption);
    return simulatedCaption;
  } catch (error) {
    console.error('❌ Error generando caption:', error);
    throw error;
  }
};

// Servicio para publicar productos (simulado)
export const publishProducts = async (
  products: ProductData[],
  selectedPlatforms: Platform[],
  customData?: any
): Promise<void> => {
  try {
    console.log('🚀 Simulacro de publicación inicializado');
    console.log('📦 Productos a publicar:', products.length);
    console.log('🌐 Plataformas seleccionadas:', selectedPlatforms.map(p => p.name));
    console.log('📋 Datos personalizados:', customData);
    
    // TODO: Implementar publicación real a las plataformas
    // Simular tiempo de procesamiento
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('✅ Publicación completada exitosamente');
  } catch (error) {
    console.error('❌ Error en la publicación:', error);
    throw error;
  }
};

// Etiquetas SEO preestablecidas
export const predefinedSEOTags = [
  'moda',
  'ropa',
  'algodón',
  'orgánico',
  'sostenible',
  'deportes',
  'casual',
  'elegante',
  'cómodo',
  'calidad',
  'tendencia',
  'clásico',
  'moderno',
  'versátil',
  'durable'
];

// Hashtags preestablecidos para redes sociales
export const predefinedHashtags = [
  'moda',
  'fashion',
  'style',
  'outfit',
  'trending',
  'ootd',
  'fashionista',
  'style',
  'look',
  'outfit',
  'fashion',
  'style',
  'trendy',
  'chic',
  'elegant'
];
